import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, pluck } from 'rxjs/operators';
import { of, from } from 'rxjs';

import * as fromActions from './application-settings.actions';
import { ApplicationSettingsService } from '../application-settings.service';
import { OfflineFileService } from 'app/core/services/offline-file.service';
import { ApplicationSettings } from './application-settings.reducer';
import {
  writeToAudioFileSuccess,
  removeOfflineDeliverySuccess,
  downloadDeliveryImageSuccess,
} from 'app/core/store/actions/offline.actions';

@Injectable()
export class ApplicationSetiingsEffects {
  actions$ = inject(Actions);
  appSettingsService = inject(ApplicationSettingsService);
  offlineFileService = inject(OfflineFileService);

  loadCacheOptions$ = createEffect(() =>
  this.actions$.pipe(
    ofType(fromActions.loadCacheOptions),
    switchMap(() => this.appSettingsService.getCacheSize().pipe(
      map((cacheOptions: any) => fromActions.loadCacheOptionsSuccess({ cacheOptions })),
      catchError(() => of(fromActions.loadCacheOptionsFailed())),
    )),
  ));

  setCacheOption$ = createEffect(() =>
    this.actions$.pipe(
    ofType(fromActions.setCacheOption),
    pluck('optionId'),
    switchMap((optionId: number) => this.appSettingsService.updateCacheSize(optionId).pipe(
      map(() => fromActions.setCacheOptionSuccess({ optionId })),
      catchError(() => of(fromActions.setCacheOptionFailed())),
    )),
  ));

  setWifiOnly$ = createEffect(() =>
    this.actions$.pipe(
    ofType(fromActions.setWifiOnly),
    pluck('wifiOnly'),
    switchMap((wifiOnly: boolean) => this.appSettingsService.setWifiOnly(wifiOnly).pipe(
      map(() => fromActions.setWifiOnlySuccess({ wifiOnly })),
      catchError(() => of(fromActions.setWifiOnlyFailed())),
    )),
  ));

  setOfflinePlayback$ =  createEffect(() =>
    this.actions$.pipe(
    ofType(fromActions.setOfflinePlayback),
    pluck('isOfflineCacheEnabled'),
    switchMap((isOfflineCacheEnabled: boolean) => (
      this.appSettingsService.setOfflineCache(isOfflineCacheEnabled).pipe(
        map(() => fromActions.setOfflinePlaybackSuccess({ isOfflineCacheEnabled })),
        catchError(() => of(fromActions.setOfflinePlaybackFailed())),
      )
    )),
  ));

  loadApplicationSettings$ = createEffect(() =>
    this.actions$.pipe(
    ofType(fromActions.loadApplicationSettings),
    switchMap(() => this.appSettingsService.loadApplicationSettings().pipe(
      map((applicationSettings: ApplicationSettings) =>
        fromActions.loadApplicationSettingsSuccess({ applicationSettings }),
      ),
      catchError(() => of(fromActions.loadApplicationSettingsFailed())),
    )),
  ));

  refreshCacheSize$ = createEffect(() =>
    this.actions$.pipe(
    ofType(
      fromActions.refreshCacheSize,
      writeToAudioFileSuccess,
      removeOfflineDeliverySuccess,
      downloadDeliveryImageSuccess,
    ),
    switchMap(() => from(this.offlineFileService.getCacheSize()).pipe(
      map((cacheSize: number) => fromActions.refreshCacheSizeSuccess({ cacheSize })),
      catchError(() => of(fromActions.refreshCacheSizeFailed())),
    )),
  ));
}
